"use client";

import { createClient } from "../supabase/client";
import { loadWorkspaceEntityTarget } from "./workspaceEntityTarget";
import type { Issue } from "../types";

export type WorkspaceIssueReferenceTarget = { kind: "id" | "identifier"; value: string };
export type WorkspaceIssueReferenceResult =
  | { status: "found"; issue: Issue }
  | { status: "unavailable" }
  | { status: "error" };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IDENTIFIER = /^[A-Z][A-Z0-9]*-[1-9][0-9]*$/i;

/** Resolve only a named target under the browser session's RLS. No global
 * inventory mutation, workspace discovery, or distinction between missing and denied. */
export async function loadWorkspaceIssueReference({ workspaceId, userId, target, signal }: {
  workspaceId: string;
  userId: string;
  target: WorkspaceIssueReferenceTarget;
  signal?: AbortSignal;
}): Promise<WorkspaceIssueReferenceResult> {
  if (signal?.aborted) return { status: "error" };
  if (!UUID.test(workspaceId) || !UUID.test(userId)
    || (target.kind === "id" ? !UUID.test(target.value) : !IDENTIFIER.test(target.value))) {
    return { status: "unavailable" };
  }
  const expectedWorkspace = workspaceId.toLowerCase();
  const expectedUser = userId.toLowerCase();
  const expectedIdentifier = target.kind === "identifier" ? target.value.toUpperCase() : null;
  try {
    const client = createClient();
    const { data: { user }, error: authError } = await client.auth.getUser();
    if (signal?.aborted || authError || user?.id !== expectedUser) return { status: "error" };
    let issueId = target.kind === "id" ? target.value.toLowerCase() : null;
    if (expectedIdentifier) {
      let query = client.from("issues").select("id,workspace_id,identifier,deleted_at")
        .eq("workspace_id", expectedWorkspace).eq("identifier", expectedIdentifier).is("deleted_at", null);
      if (signal) query = query.abortSignal(signal);
      const { data: row, error } = await query.maybeSingle();
      if (signal?.aborted || error) return { status: "error" };
      if (!row || !UUID.test(row.id) || row.workspace_id !== expectedWorkspace
        || row.identifier !== expectedIdentifier || row.deleted_at !== null) return { status: "unavailable" };
      issueId = row.id;
    }
    if (!issueId) return { status: "unavailable" };
    const result = await loadWorkspaceEntityTarget({ workspaceId: expectedWorkspace, kind: "issue", id: issueId, signal });
    if (signal?.aborted) return { status: "error" };
    if (result.status !== "found") return result;
    if (result.kind !== "issue" || result.entity.id !== issueId || result.entity.workspaceId !== expectedWorkspace
      || (expectedIdentifier && result.entity.identifier !== expectedIdentifier)) return { status: "unavailable" };
    const verified = await client.auth.getUser();
    if (signal?.aborted || verified.error || verified.data.user?.id !== expectedUser) return { status: "error" };
    return { status: "found", issue: result.entity };
  } catch {
    return { status: "error" };
  }
}
